import { useMemo } from "react"
import { type OperationContext, type OperationResult, useMutation, useQuery } from "urql"

import type { CompanyAccountingTerm, CompanyAccountingTermMutationUpsertArgs } from "@/graphql/codegen/graphql"
import { AddCompanyAccountingTermDocument, CompanyAccountingTermListDocument } from "@/graphql/codegen/graphql"
import type { Mutation, Query } from "@/graphql/codegen/graphql.ts"

interface UseAccountingTermsProps {
  companyId?: string
  pause?: boolean
}

const useCompanyAccountingTerms = ({ companyId = "", pause = false }: UseAccountingTermsProps = {}): {
  accountingTermsList: CompanyAccountingTerm[]
  accountingTermsFetching: boolean
  refetchAccountingTermsList: () => void
  addAccountingTerm: (
    variables: CompanyAccountingTermMutationUpsertArgs,
    context?: Partial<OperationContext>
  ) => Promise<OperationResult<Pick<Mutation, "companyAccountingTerm">, CompanyAccountingTermMutationUpsertArgs>>
} => {
  const [{ data, fetching: accountingTermsFetching }, executeQuery] = useQuery<Pick<Query, "companyAccountingTerm">>({
    query: CompanyAccountingTermListDocument,
    variables: { input: { companyId } },
    pause: pause || !companyId,
  })

  const refetchAccountingTermsList = () => {
    executeQuery({ requestPolicy: "network-only" })
  }

  const accountingTermsList = useMemo(() => {
    if (!data?.companyAccountingTerm?.list) {
      return []
    }
    return data.companyAccountingTerm.list.__typename === "CompanyAccountingTermListSuccess"
      ? (data.companyAccountingTerm.list.companyAccountingTerm ?? [])
      : []
  }, [data]) as CompanyAccountingTerm[]

  const [, addAccountingTerm] = useMutation<
    Pick<Mutation, "companyAccountingTerm">,
    CompanyAccountingTermMutationUpsertArgs
  >(AddCompanyAccountingTermDocument)

  return {
    accountingTermsList,
    accountingTermsFetching,
    refetchAccountingTermsList,
    addAccountingTerm,
  }
}

export default useCompanyAccountingTerms
